import * as React from 'react';

import { LogOut, LucideProps, UserCircle } from 'lucide-react';
import { useRouter } from 'next/router';
import { enqueueSnackbar } from 'notistack';

import { useAuth0 } from '@auth0/auth0-react';
import { Badge, Box, Divider, MenuList, MenuListProps } from '@mui/material';

import { queryAggregateTalentInvitation } from '@itp/prisma-talent/gql';
import { usePrivateApollo, useUser } from '@itp/talent';
import { Link } from '@itp/talent/components/core/link';
import { CompanyWidget } from '@itp/talent/components/layout/components/companyWidget';
import { useLocalAuth } from '@itp/talent/context/localAuthContext';

import { BodyTextBold } from '../../../core/typography';
import { TalentBoostCard } from '../../components/boostCard/talentBoostCard';
import { adminMenuItems } from './adminMenuItems';
import {
  MenuItemContent,
  SectionLabel,
  SectionLabelText,
  StyledMenuItem,
} from './asideNavigation.styles';
import { companyMenuItems } from './companyMenuItems';
import { talentMenuItems } from './talentMenuItems';

const BOOST_CARD_HIDDEN_KEY = 'talentBoostCardHidden';

export interface MenuItem {
  badge?: number;
  icon?: React.ForwardRefExoticComponent<
    Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
  >;
  iconSelected?: React.ForwardRefExoticComponent<
    Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
  >;
  isMainMenu?: boolean;
  label: string;
  link?: string;
  onClick?: () => void;
}

export type AsideNavigationType = 'admin' | 'company' | 'talent';

const VALID_MENU_TYPES: AsideNavigationType[] = ['admin', 'company', 'talent'];

interface AsideNavigationProps extends MenuListProps {
  menuType: AsideNavigationType;
}

export const AsideNavigation: React.FC<AsideNavigationProps> = ({ menuType: menuTypeProp }) => {
  const router = useRouter();
  const { user } = useUser();
  const { isAuthenticated: isAuth0Authenticated, logout: auth0Logout } = useAuth0();
  const { logout: localLogout } = useLocalAuth();

  const queryMenuType =
    typeof router.query.menuType === 'string' &&
    VALID_MENU_TYPES.includes(router.query.menuType as AsideNavigationType)
      ? (router.query.menuType as AsideNavigationType)
      : undefined;

  const menuType = queryMenuType ?? menuTypeProp;

  const [boostCardHidden, setBoostCardHidden] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(BOOST_CARD_HIDDEN_KEY) === 'true';
  });

  const hasSelectedCompany = !user.loading && menuType === 'company' && user?.selectedCompanyId;
  const isCompanyMenu = menuType === 'company';
  const isTalentMenu = menuType === 'talent';
  const isAdminMenu = menuType === 'admin';

  const { data } = usePrivateApollo<{ _count: { id: number } }>({
    query: queryAggregateTalentInvitation,
    skip: !isTalentMenu || !user?.id,
    variables: {
      where: {
        ignoredAt: { equals: null },
        talentProfileId: { equals: user?.profile?.id },
      },
    },
  });

  const isBoosted =
    isTalentMenu &&
    !!(user?.profile as any)?.boost?.boostedUntil &&
    new Date((user?.profile as any).boost.boostedUntil) > new Date();

  const handleHideBoostCard = () => {
    localStorage.setItem(BOOST_CARD_HIDDEN_KEY, 'true');
    setBoostCardHidden(true);
  };

  const handleShowBoostCard = () => {
    localStorage.removeItem(BOOST_CARD_HIDDEN_KEY);
    setBoostCardHidden(false);
  };

  const menuItems = isTalentMenu
    ? talentMenuItems({
        isBoosted,
        onBoostClick: handleShowBoostCard,
        showBoostMenuItem: boostCardHidden,
        talentInvitationCount: data?._count?.id ?? 0,
      })
    : isAdminMenu
      ? adminMenuItems({
          isAdmin: Boolean(user.isAdmin),
          isImpersonate: Boolean(user.isImpersonate),
        })
      : companyMenuItems(user);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('mySkillsSearch');
    localStorage.removeItem('personalizedSearch');
    localStorage.removeItem('jobAdDrafts');
    localLogout();
    if (isAuth0Authenticated) {
      auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    } else {
      router.replace('/sign-in');
    }
  };

  const resolvedPath = router.asPath.split('?')[0];

  const generatedMenuItems = () =>
    menuItems.map((menu: MenuItem) => {
      if (menu.isMainMenu) {
        return (
          <SectionLabel key={`section-${menu.label}`}>
            <SectionLabelText>{menu.label}</SectionLabelText>
          </SectionLabel>
        );
      }

      const isSelected = menu.link ? menu.link === resolvedPath : false;
      const IconComponent = isSelected ? menu.iconSelected || null : menu.icon || null;

      return (
        <StyledMenuItem
          component={menu.link ? Link : 'li'}
          href={menu.link}
          key={menu.label}
          onClick={menu.onClick}
          selected={isSelected}
        >
          <MenuItemContent
            className="menu-item-content"
            style={menu.badge ? { paddingRight: '24px' } : undefined}
          >
            {IconComponent ? <IconComponent size={18} /> : null}
            {menu.badge ? (
              <Badge
                sx={{
                  '.MuiBadge-badge': {
                    alignSelf: 'center',
                    position: 'relative',
                    top: '10px',
                  },
                }}
                badgeContent={menu.badge}
                color="primary"
              >
                <BodyTextBold>{menu.label}</BodyTextBold>
              </Badge>
            ) : (
              <BodyTextBold>{menu.label}</BodyTextBold>
            )}
          </MenuItemContent>
        </StyledMenuItem>
      );
    });

  return (
    <Box
      component="nav"
      style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 6, width: '100%' }}
    >
      {isCompanyMenu && hasSelectedCompany && (
        <CompanyWidget
          companyLogo={
            user?.selectedCompany?.company?.logo?.optimizedUrl ||
            user?.selectedCompany?.company?.logo?.originalUrl
          }
          onShareClick={() => {
            const url = new URL(window.location.href);
            url.searchParams.set('company_id', user.selectedCompanyId as string);
            navigator.clipboard
              .writeText(url.toString())
              .then(() => enqueueSnackbar('URL copied to clipboard', { autoHideDuration: 3000 }))
              .catch((err) => console.error('Failed to copy URL: ', err));
          }}
          companyName={user?.selectedCompany?.company?.name || ''}
        />
      )}
      {isTalentMenu && !boostCardHidden && <TalentBoostCard onHide={handleHideBoostCard} />}
      {(hasSelectedCompany || isTalentMenu || isAdminMenu) && (
        <MenuList suppressHydrationWarning>{generatedMenuItems()}</MenuList>
      )}
      <Divider />
      <Box py={1}>
        <StyledMenuItem
          component={Link}
          href={`/account?menuType=${menuType}`}
          selected={resolvedPath === '/account'}
        >
          <MenuItemContent className="menu-item-content">
            <UserCircle size={18} />
            <BodyTextBold>Account</BodyTextBold>
          </MenuItemContent>
        </StyledMenuItem>
        <StyledMenuItem onClick={handleLogout}>
          <MenuItemContent className="menu-item-content">
            <LogOut size={18} />
            <BodyTextBold>Logout</BodyTextBold>
          </MenuItemContent>
        </StyledMenuItem>
      </Box>
    </Box>
  );
};
